import { memo } from "react";
import classNames from "classnames/bind";
import styles from "./HeaderNavItemsList.module.scss";

import SmartLink from "common/wrapper/SmartLink";
import type { ResolvedNavItem } from "resolvers/base/navigationResolver";

const cx = classNames.bind(styles);

type HeaderNavItemsListProps = {
    items: ResolvedNavItem[];
    activeSubMenu: string | null;
    activeMegaMenu: string | null;
    priority: string;
    handleToggleSubMenu: (key: string | null) => void;
    handleToggleMegaMenu: (key: string | null) => void;
    handleCancelToggleSubMenu?: () => void;
    handleCancelToggleMegaMenu?: () => void;
    prefetch?: boolean;
};

const HeaderNavItemsList: React.FC<HeaderNavItemsListProps> = ({
    items,
    priority,
    activeSubMenu,
    activeMegaMenu,
    handleToggleSubMenu,
    handleToggleMegaMenu,
    handleCancelToggleSubMenu,
    handleCancelToggleMegaMenu,
    prefetch = false,
}) => {
    if (!items?.length) return null;

    return (
        <ul className={cx("list", priority)}>
            {items.map(({ link, text, subMenuId, megaMenuId }) => {
                const hasMegaMenu = !!megaMenuId;

                const handleAction = !hasMegaMenu ? handleToggleSubMenu : handleToggleMegaMenu;
                const actionKey = !hasMegaMenu ? subMenuId : megaMenuId;
                const active = !hasMegaMenu
                    ? activeSubMenu === subMenuId
                    : activeMegaMenu === megaMenuId;

                const handleMouseEnter = () => {
                    if (hasMegaMenu) handleToggleSubMenu(null);
                    if (!hasMegaMenu) handleToggleMegaMenu(null);
                    handleAction(actionKey ?? null);
                };

                const handleMouseExit = () => {
                    handleCancelToggleSubMenu?.();
                    handleCancelToggleMegaMenu?.();
                };

                return (
                    <li
                        key={text}
                        className={cx("item", { active }, subMenuId ? "item-with-submenu" : "")}
                    >
                        {link && !subMenuId ? (
                            <SmartLink
                                href={link}
                                prefetch={!!prefetch}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseExit}
                            >
                                <a className={cx("link")}>{text}</a>
                            </SmartLink>
                        ) : (
                            <span
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseExit}
                                className={cx("link")}
                            >
                                {text}
                            </span>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

export default memo(HeaderNavItemsList);
